/**
 * SvelteKit Client Hooks
 * Initializes Sentry for client-side error tracking
 *
 * Per-Survey Configuration:
 * Add to survey YAML config section for granular control:
 *
 *   config:
 *     disableErrorTracking: true       # Disable all error tracking (default: false)
 *     enableSessionReplay: true        # Enable session replay (default: false)
 *     enablePerformanceTracking: true  # Enable performance tracking (default: false)
 *
 * By default: Error tracking ENABLED, Session replay DISABLED, Performance tracking DISABLED (privacy-first)
 */

import * as Sentry from '@sentry/sveltekit';
import { handleErrorWithSentry } from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';

// Get configuration from environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
const ENV = import.meta.env.VITE_ENV || import.meta.env.MODE || 'production';
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE;

/**
 * Get config flag from window.__MOUI_CONFIG__ (set by +layout.svelte during SSR)
 * This allows checking YAML config flags before Sentry initializes
 */
function getConfigFlag(key: string): boolean {
	if (typeof window !== 'undefined' && (window as any).__MOUI_CONFIG__) {
		return (window as any).__MOUI_CONFIG__[key] === true;
	}
	return false;
}

// Only initialize Sentry in production/staging environments
// Skip if running locally (localhost/127.0.0.1), but allow staging domains even in dev mode
const isLocalDev = window.location.hostname === 'localhost' ||
                   window.location.hostname.startsWith('127.0.0.') ||
                   window.location.hostname.startsWith('192.168.');

const shouldInitialize = SENTRY_DSN &&
                        SENTRY_DSN.trim() !== '' &&
                        !isLocalDev &&
                        !getConfigFlag('disableErrorTracking');

if (shouldInitialize) {
	// Check if session replay should be enabled
	const enableReplay = getConfigFlag('enableSessionReplay');
	const enablePerformanceTracking = getConfigFlag('enablePerformanceTracking');

	const sentryConfig: any = {
		dsn: SENTRY_DSN,
		environment: ENV,
		release: RELEASE,
		autoSessionTracking: false,
		tracesSampleRate: enablePerformanceTracking ? 0.1 : 0,
		sampleRate: 1.0,
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0,

		beforeSendTransaction(event) {
			// Only send transactions if performance tracking is enabled
			if (!enablePerformanceTracking) {
				return null;
			}
			if (event.transaction && typeof window !== 'undefined') {
				event.transaction = window.location.href;
			}
			return event;
		},

		// Before send hook - GDPR compliance, PII scrubbing, and survey context
		beforeSend(event, hint) {
			// Double-check: Don't send from local development
			if (isLocalDev) {
				return null;
			}

			// Double-check: Don't send if error tracking is disabled for this survey
			if (getConfigFlag('disableErrorTracking')) {
				return null;
			}

			// Replace SvelteKit route template with full URL
			// SvelteKit sets transaction to "/s/[questionId]", we want full URL with domain
			if (event.transaction && typeof window !== 'undefined') {
				event.transaction = window.location.href;
			}

			// Scrub user PII - only keep anonymous identifiers
			if (event.user) {
				delete event.user.email;
				delete event.user.username;
				delete event.user.ip_address;
				// Keep only id if it's a non-PII identifier
			}

			// Replace request URL with actual resolved URL (not SvelteKit route template)
			// SvelteKit's event.request.url contains templates like /s/[questionId]
			// We want the actual URL like /s/123
			if (event.request) {
				try {
					const actualUrl = new URL(window.location.href);
					const safeParams = ['preview', 'locale', 'page', 'lang'];
					const scrubbedParams = new URLSearchParams();

					for (const [key, value] of actualUrl.searchParams.entries()) {
						if (safeParams.includes(key.toLowerCase())) {
							scrubbedParams.set(key, value);
						} else {
							scrubbedParams.set(key, '[Filtered]');
						}
					}

					actualUrl.search = scrubbedParams.toString();
					event.request.url = actualUrl.toString();
				} catch (e) {
					// Invalid URL, leave as is
				}
			}

			// Scrub request headers
			if (event.request?.headers) {
				const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key'];
				for (const header of sensitiveHeaders) {
					if (event.request.headers[header]) {
						event.request.headers[header] = '[Filtered]';
					}
				}
			}

			// Scrub breadcrumbs - remove all input values
			if (event.breadcrumbs) {
				for (const breadcrumb of event.breadcrumbs) {
					if (breadcrumb.category === 'ui.input' && breadcrumb.data) {
						delete breadcrumb.data.value;
					}
					// Scrub console messages for common PII patterns
					if (breadcrumb.category === 'console' && breadcrumb.message) {
						breadcrumb.message = breadcrumb.message
							.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]')
							.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[phone]')
							.replace(/eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g, '[jwt]');
					}
				}
			}

			// Scrub exception messages for common PII patterns
			if (event.exception?.values) {
				for (const exception of event.exception.values) {
					if (exception.value) {
						exception.value = exception.value
							.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]')
							.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[phone]')
							.replace(/eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g, '[jwt]');
					}
				}
			}

			// Extract survey context from sessionStorage (non-PII technical data only)
			try {
				const storedSurvey = sessionStorage.getItem('walku:survey');
				if (storedSurvey) {
					const parsed = JSON.parse(storedSurvey);

					// Set survey tags (technical identifiers only, no user data)
					const tags: Record<string, string> = {};

					if (parsed.surveyId) tags.surveyId = parsed.surveyId;
					if (parsed.survey?.config?.id) tags.surveyId = parsed.survey.config.id;
					if (parsed.survey?.config?.collection) tags.collection = parsed.survey.config.collection;
					if (parsed.survey?.config?.database) tags.database = parsed.survey.config.database;

					// Add domain and full URL (technical info, PII already scrubbed above)
					tags.domain = window.location.hostname;
					tags.url = event.request?.url || window.location.href;

					// Determine route type
					const pathname = window.location.pathname;
					if (pathname.startsWith('/stats')) {
						tags.route_type = 'stats';
					} else if (pathname.startsWith('/auth')) {
						tags.route_type = 'auth';
					} else if (pathname.startsWith('/profile')) {
						tags.route_type = 'profile';
					} else if (pathname.startsWith('/s/') || pathname === '/' || pathname.match(/^\/\d+\/?$/)) {
						tags.route_type = 'survey';
					} else {
						tags.route_type = 'other';
					}

					event.tags = { ...event.tags, ...tags };

					// Set survey context (technical data only)
					event.contexts = event.contexts || {};
					event.contexts.survey = {
						surveyId: parsed.surveyId || parsed.survey?.config?.id,
						collection: parsed.survey?.config?.collection,
						database: parsed.survey?.config?.database,
						domain: window.location.hostname,
						route_type: tags.route_type
					};
				}
			} catch (error) {
				// Don't fail if we can't extract context
				console.warn('[Sentry] Failed to extract survey context:', error);
			}

			return event;
		}
	};

	// Add replay integration only if enabled
	if (enableReplay) {
		sentryConfig.integrations = [
			Sentry.replayIntegration({
				maskAllText: true,
				blockAllMedia: true,
				maskAllInputs: true
			})
		];
		sentryConfig.replaysSessionSampleRate = 0.2;
		sentryConfig.replaysOnErrorSampleRate = 1.0;
	}

	Sentry.init(sentryConfig);
}

// ALWAYS attach global error handlers (even in dev mode)
// In dev: log to console only
// In prod with Sentry: log to console AND send to Sentry
if (typeof window !== 'undefined') {
	// Set transaction name to full URL (not SvelteKit route template)
	// Include domain since each survey deploys to different subdomains
	if (shouldInitialize && Sentry) {
		// Set initial transaction name with full URL
		Sentry.getCurrentScope().setTransactionName(window.location.href);

		// Update on navigation (for SPA route changes)
		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;

		history.pushState = function(...args) {
			originalPushState.apply(this, args);
			Sentry.getCurrentScope().setTransactionName(window.location.href);
		};

		history.replaceState = function(...args) {
			originalReplaceState.apply(this, args);
			Sentry.getCurrentScope().setTransactionName(window.location.href);
		};

		window.addEventListener('popstate', () => {
			Sentry.getCurrentScope().setTransactionName(window.location.href);
		});
	}

	window.addEventListener('error', (event) => {
		console.error('[Global Error Handler]', event.error);

		if (shouldInitialize && Sentry) {
			Sentry.captureException(event.error);
		}
	});

	window.addEventListener('unhandledrejection', (event) => {
		console.error('[Global Unhandled Rejection]', event.reason);

		if (shouldInitialize && Sentry) {
			Sentry.captureException(event.reason);
		}
	});
}

// Export the error handler for SvelteKit-specific errors (load functions, etc.)
export const handleError: HandleClientError = handleErrorWithSentry();
